import { useEffect, useRef, useState } from "react";

const ADSENSE_ID = "ca-pub-4318140854336417";

/**
 * Injects AdSense lazily — only after the user interacts with the page
 * (scroll, pointer, keypress, touch) OR when the ad slot scrolls near the
 * viewport. This avoids the ~200KB+ adsbygoogle.js blocking initial load
 * and significantly improves Lighthouse Performance / Best Practices scores.
 */
const GoogleAdSense = ({ slot, format = "auto" }: { slot?: string; format?: string }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  // Wait for user interaction OR near-viewport visibility before loading
  useEffect(() => {
    if (!ADSENSE_ID || shouldLoad) return;

    let triggered = false;
    const trigger = () => {
      if (triggered) return;
      triggered = true;
      setShouldLoad(true);
      cleanup();
    };

    const events: (keyof WindowEventMap)[] = [
      "scroll",
      "pointerdown",
      "pointermove",
      "keydown",
      "touchstart",
    ];
    const opts: AddEventListenerOptions = { once: true, passive: true, capture: true };
    events.forEach((e) => window.addEventListener(e, trigger, opts));

    let io: IntersectionObserver | null = null;
    if (containerRef.current && "IntersectionObserver" in window) {
      io = new IntersectionObserver(
        (entries) => {
          if (entries.some((en) => en.isIntersecting)) trigger();
        },
        { rootMargin: "400px" }
      );
      io.observe(containerRef.current);
    }

    // Safety net: load after 8s of idle so ads still render for non-interacting bots/users
    const fallback = window.setTimeout(trigger, 8000);

    function cleanup() {
      events.forEach((e) => window.removeEventListener(e, trigger, opts));
      if (io) io.disconnect();
      window.clearTimeout(fallback);
    }

    return cleanup;
  }, [shouldLoad]);

  // Inject script + push ad once permitted
  useEffect(() => {
    if (!shouldLoad || !ADSENSE_ID) return;
    if (!document.querySelector(`script[src*="adsbygoogle"]`)) {
      const s = document.createElement("script");
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_ID}`;
      s.async = true;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    }
    try {
      ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
    } catch {}
  }, [shouldLoad]);

  if (!ADSENSE_ID) return null;

  return (
    <div ref={containerRef} className="my-8 text-center min-h-[120px]">
      {shouldLoad && (
        <ins
          className="adsbygoogle"
          style={{ display: "block" }}
          data-ad-client={ADSENSE_ID}
          data-ad-slot={slot}
          data-ad-format={format}
          data-full-width-responsive="true"
        />
      )}
      <p className="text-[10px] text-muted-foreground font-body mt-1">Advertisement</p>
    </div>
  );
};

export default GoogleAdSense;
